import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Mail, Lock, User, Eye, EyeOff, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/j9ko1qni_70964783-6509-4567-B714-2BDADDC4579E%20%281%29.PNG";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error("Please accept the terms and indemnity agreement");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password });
      login(response.data);
      toast.success("Welcome to KiddiPlay Club!");
      navigate("/onboarding");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Colorful Header Bar - Same as Landing */}
      <header className="relative z-50 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between px-6 py-3">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="KiddiPlay" className="w-10 h-10 rounded-full border-2 border-white/30" />
              <span className="text-xl font-bold text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>
                KiddiPlay Club
              </span>
            </Link>
            
            <div className="flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-white/20">
                  Log in
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-64px)]">
        {/* Decorative blobs */}
        <div className="blob blob-purple w-80 h-80 -top-40 -left-40 fixed" />
        <div className="blob blob-pink w-64 h-64 bottom-20 -right-32 fixed" />

        {/* Left side - Fun Image */}
        <div className="hidden lg:block flex-1 relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1533222535026-754c501569dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwY2hpbGRyZW4lMjBncm91cCUyMGZyaWVuZHMlMjBvdXRkb29yJTIwcGxheWluZyUyMGxhdWdoaW5nfGVufDB8fHx8MTc4MjczODY5Mnww&ixlib=rb-4.1.0&q=85"
            alt="Diverse children playing together on grass field"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#FFF9F5]/80" />
          
          {/* Overlay content */}
          <div className="absolute bottom-12 left-12 right-12">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl max-w-md">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white" fill="white" />
                </div>
                <p className="font-semibold text-slate-800">New Match!</p>
              </div>
              <p className="text-slate-600 text-sm">
                "We met the loveliest family through KiddiPlay - the kids are now best friends and we've got a proper support network!" 
                <span className="block mt-2 text-purple-600 font-medium">— Sarah, London</span>
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <h1 className="text-3xl font-bold mb-2 text-center" style={{ fontFamily: 'Fredoka, Nunito, sans-serif', color: '#6B4D8A' }}>
              Join the Club!
            </h1>
            <p className="text-slate-600 mb-8 text-center">Create your account and start connecting with families near you</p>

            {/* Google Login */}
            <Button
              variant="outline"
              className="w-full rounded-xl py-6 mb-6 border-2 hover:bg-slate-50"
              onClick={handleGoogleLogin}
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#FFF9F5] text-slate-500">or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-slate-700">Your Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Sarah Johnson"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-12 h-12 rounded-xl bg-white border-slate-200 focus:border-purple-400 focus:ring-purple-200"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="hello@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 rounded-xl bg-white border-slate-200 focus:border-purple-400 focus:ring-purple-200"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-12 rounded-xl bg-white border-slate-200 focus:border-purple-400 focus:ring-purple-200"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">At least 6 characters</p>
            </div>

            {/* Terms & Indemnity Agreement */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <button
                type="button"
                onClick={() => setAcceptedTerms(!acceptedTerms)}
                className="flex items-start gap-3 text-left w-full"
              >
                {acceptedTerms ? (
                  <CheckSquare className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <Square className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                )}
                <div className="text-xs text-slate-600">
                  <p className="font-medium text-slate-700 mb-1">I understand and agree that:</p>
                  <ul className="space-y-1 text-slate-500">
                    <li>• KiddiPlay is a social platform - NOT a childcare, childminding, or guardian service</li>
                    <li>• Any meetups or playdates are arranged at my own discretion and parental responsibility</li>
                    <li>• KiddiPlay holds no legal responsibility or insurance cover for activities</li>
                  </ul>
                  
                  <p className="font-medium text-slate-700 mt-3 mb-1">Safe meetup guidelines (recommended):</p>
                  <ul className="space-y-1 text-slate-500">
                    <li>• Under 2s: We recommend no more than 3 little ones per supervising adult</li>
                    <li>• Ages 2-3: Up to 4 children per adult</li>
                    <li>• Ages 3-8: Up to 8 children per adult</li>
                    <li>• Over 8s: Use your judgement based on the activity and setting</li>
                  </ul>
                  
                  <p className="mt-2">
                    I agree to the{" "}
                    <Link to="/terms" className="text-purple-600 underline" onClick={(e) => e.stopPropagation()}>Terms</Link>,{" "}
                    <Link to="/privacy" className="text-purple-600 underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>, and{" "}
                    <Link to="/indemnity" className="text-purple-600 underline" onClick={(e) => e.stopPropagation()}>Indemnity Agreement</Link>.
                  </p>
                </div>
              </button>
            </div>

            <Button
              type="submit"
              className="w-full hero-gradient text-white rounded-xl py-6 text-lg font-medium disabled:opacity-50"
              disabled={loading || !acceptedTerms}
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account..." : "Join KiddiPlay Club"}
            </Button>
          </form>

          <p className="text-center mt-6 text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-purple-500 hover:text-purple-600 font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>

          <p className="text-center mt-4 text-xs text-slate-400">
            For families with children aged 0-16 years. Registered with the ICO.
          </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
